import React, { useState } from 'react';
import { Checkbox, Modal, message } from 'antd';
import { theme } from 'utils/style/theme';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import iconClose from 'utils/icon/icon_close_circle.svg';
import { handleMessage } from 'utils/helper/message';
import PrimaryButton from 'components/button/PrimaryButton';
import InputStyled from 'components/InputStyled';
import { charactersApis } from 'apis/characters/charactersApis';
import InputWithCounter from 'components/input/InputWithCounter';
import { DefaultInputCountConfig } from 'utils/types/storyTypes';

/**
 * * 등장인물 새 능력 생성 모달
 */

interface IProps {
  visible: {
    createCharacter: boolean;
    createAbility: boolean;
    editAiblity: boolean;
    delete: boolean;
    name: string;
  };
  setVisible: (e: any) => void;
  setRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  refresh: boolean;
  projectId: string | undefined;
  data: any;
}

const CharactersCreateAbility = ({
  visible,
  setVisible,
  setRefresh,
  refresh,
  projectId,
  data,
}: IProps) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState({
    name: '',
    min: 0,
    max: 0,
  });
  const [isMain, setIsMain] = useState<boolean>(true);
  const [loading, setLoading] = useState(false);

  /// 서버 전송
  const handleSubmit = async () => {
    if (projectId) {
      try {
        const postData = {
          ability_name: inputValue.name,
          min_value: Number(inputValue.min),
          max_value: Number(inputValue.max),
          is_main: isMain,
        };
        setLoading(true);
        await charactersApis.characterCreateAbility(
          projectId,
          data.id,
          postData,
        );
        setRefresh(!refresh);
        setLoading(false);
        handleClose();
      } catch (err) {
        setLoading(false);
        handleMessage(message, 'error', 'Upload failed');
      }
    }
  };

  /// 모달 닫기
  const handleClose = () => {
    setVisible({ ...visible, createAbility: false });
    setInputValue({
      name: '',
      min: 0,
      max: 0,
    });
    // 폼을 비워줘야 이미지 첨부한 것도 비워진다.
  };

  const handleInputChange = (value, type) => {
    const cloneData = JSON.parse(JSON.stringify(inputValue));
    cloneData[type] = value;
    setInputValue(cloneData);
  };

  const handleCheckbox = (value) => {
    setIsMain(value);
  };

  return (
    <CustomModal
      title={t(
        'storyEpisodeID.storyTabMenuList.charactersContent.modal.createAbility.title',
      )}
      open={visible.createAbility}
      onCancel={handleClose}
      footer={
        <ButtonWrapper>
          <PrimaryButton
            text={t('buttons.create')}
            height='40px'
            width='fit-content'
            bgColor={theme.colors.blue_500}
            textColor='#ffffff'
            fontWeight={theme.fontWeights.medium}
            fontSize={theme.fontSizes.normal}
            border={`1px solid ${theme.colors.blue_500}`}
            rounded='6px'
            hoverBgColor={theme.colors.blue_600}
            activeBgColor={theme.colors.blue_800}
            selectedBgColor={theme.colors.blue_700}
            onClick={handleSubmit}
            loading={loading}
            disabled={loading}
          />
        </ButtonWrapper>
      }
      closeIcon={
        <img src={iconClose} width={20} height={20} alt='icon-close' />
      }
    >
      <SectionWarpper>
        <div>
          <p className='tips'>
            등장인물로 등록된 캐릭터는 필수로 최소 1개의 능력을 등록해야합니다.
          </p>
        </div>
        <div>
          <p className='title'>
            {t(
              'storyEpisodeID.storyTabMenuList.charactersContent.modal.createAbility.name',
            )}
          </p>
          <InputWithCounter
            value={inputValue.name}
            onChange={(e) => {
              handleInputChange(e.target.value, 'name');
            }}
            maxLength={30}
            placeholder='스크립트에서 사용되는 능력 이름을 등록합니다. (EX:호감도)'
            count={DefaultInputCountConfig}
          />
        </div>
        <div className='input-section'>
          <div className='input-section-box'>
            <p className='title'>선물하기로 올릴 수 있는 능력 상한값</p>
            <InputStyled
              value={inputValue.min}
              onChange={(e) => {
                handleInputChange(e.target.value, 'min');
              }}
              type='number'
            />
          </div>
          <div className='input-section-box'>
            <p className='title'>능력수치 MAX 값</p>
            <InputStyled
              value={inputValue.max}
              onChange={(e) => {
                handleInputChange(e.target.value, 'max');
              }}
              type='number'
            />
          </div>
        </div>
        <div className='checkbox-section'>
          <div className='checkbox-item'>
            <p>
              {t(
                'storyEpisodeID.storyTabMenuList.charactersContent.modal.createAbility.main',
              )}
            </p>
            <Checkbox
              checked={isMain}
              onChange={(e) => {
                handleCheckbox(e.target.checked);
              }}
            />
          </div>
        </div>
      </SectionWarpper>
      <p className='title'>
        (**캐릭터에게 여러개의 능력이 필요한 경우는 하나만 주 능력 설정이
        되어야합니다.)
      </p>
    </CustomModal>
  );
};

export default CharactersCreateAbility;

const CustomModal = styled(Modal)`
  .ant-modal-body {
    padding: 0;
  }
  .ant-modal-content {
    box-shadow: none;
    width: 566px;
  }
  .ant-radio-wrapper {
    span {
      font-size: ${theme.fontSizes.normal};
      font-weight: 400;
      line-height: 24px;
      letter-spacing: 0.2px;
      color: ${theme.colors.dark_500};
    }
  }
  .ant-modal-footer {
    margin-top: 10px !important;
  }
  ul {
    margin-left: 25px;
  }
  .ant-modal-close:hover {
    background-color: transparent;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: end;
`;

const SectionWarpper = styled.div`
  & > * + * {
    margin-top: 8px;
  }
  .checkbox-section {
    display: flex;
    align-items: center;
    & > * + * {
      margin-left: 48px;
    }
  }
  .input-section {
    display: flex;
    align-items: center;
    justify-content: space-between;
    & > * + * {
      margin-left: 8px;
    }
  }
  .checkbox-item {
    display: flex;
    align-items: center;
    p {
      font-size: ${theme.fontSizes.sm};
      font-weight: ${theme.fontWeights.normal};
      line-height: 20px;
      letter-spacing: 0.2px;
      color: #000000;
    }
    & > * + * {
      margin-left: 8px;
    }
  }
  .input-section-box {
    width: 50%;
  }
`;
